import { getAllCasesAndEvents, setCurrentCaseId } from '../screens/cases/actions';
import {
  COLD_START_FINISHED,
  FORCE_FULL_SYNC,
  FULL_SYNC_SUCCESS,
  SET_LANGUAGE_PREF,
  SET_RESET_PASSWORD_TOKEN,
  WEBSOCKET_UPDATE_TIMESTAMP,
  CLEAR_ALL_STATE,
} from './actionTypes';
import { registerForNotifications } from '../firebase/actions';
import { InteractionManager } from 'react-native';
import { GIT_COMMIT_KEY } from '../constants/asyncStorageKeys';
import { fetchItem, storeItem } from '../store/asyncStorageAPI';
import { logoutUser, fetchInviteAuthDetails } from '../screens/auth/actions';
import { getUserInfoBasedOnProfileChange } from '../screens/userProfile/actions';
import { GIT_COMMIT, FORCE_USER_LOGOUT, BASE_URL, env } from '../constants/env';
import { __WEB__ } from '../constants/styles';

import Navigation from '../navigation';
import Utils from '../utils';
import { checkAndDisplayNewAppVersion } from '../notificationsHandlers';

import { sqlNow } from '../services/commonService';

import {
  AUTH_RESETPASSWORD,
  NEW_INVITE_NOTIFICATION,
  SEND_INVITE_NOTIFICATION,
  NEW_EMPLOYER_INVITE_NOTIFICATION,
} from '../notificationsHandlersTypes';

let _showingSyncOverlay = false;
let _syncOverlayTimeoutId = null;
let _appVersionTimeoutId = null;

function _showRegisterScreen() {
  Navigation.openScreen({
    screen: 'Register',
    title: 'Register',
    passProps: {},
  });
}

export function showSyncOverlay() {
  if (_showingSyncOverlay) return;
  // we've already schedule this
  if (_syncOverlayTimeoutId) return;

  _syncOverlayTimeoutId = setTimeout(() => {
    _showingSyncOverlay = true;
    _syncOverlayTimeoutId = null;

    Navigation.showLightBox({ screen: 'SyncOverlay' });
  }, 200);
}

export function hideSyncOverlay() {
  if (!_showingSyncOverlay) {
    if (_syncOverlayTimeoutId) clearTimeout(_syncOverlayTimeoutId);
    _syncOverlayTimeoutId = null;
    return;
  }
  _showingSyncOverlay = false;
  Navigation.dismissLightBox();
}

export function handleBranchLinkLaunch(params) {
  return function (dispatch, getState) {
    Utils.trackCampaign(params['~campaign'], params['~channel']);
    const resource = params.resource;
    // console.info('branch params ', params);

    switch (resource) {
      case NEW_EMPLOYER_INVITE_NOTIFICATION:
      case NEW_INVITE_NOTIFICATION:
        dispatch(handleBranchInviteLaunch(params));
        break;
      case SEND_INVITE_NOTIFICATION:
        Navigation.showModal({
          screen: 'Invite',
          title: 'INVITE PEOPLE',
        });
        break;
      case AUTH_RESETPASSWORD:
        dispatch(logoutUser(true))
          .then(() => {
            // web doesn't want this as a modal
            dispatch({ type: SET_RESET_PASSWORD_TOKEN, payload: params.resetPasswordToken });
            InteractionManager.runAfterInteractions(() => {
              Navigation.openScreen(
                {
                  screen: 'ResetPassword',
                  title: '',
                  animationType: 'none',
                },
                null,
                !__WEB__
              );
            });
          })
          .catch((e) => {
            console.info('reset promise error', e);
          });
        break;
      default:
        break;
    }
  };
}

function handleBranchInviteLaunch(params) {
  return async function (dispatch, getState) {
    const resource = params.resource;

    let fullSync = getState().user.fullSync;

    switch (resource) {
      case NEW_EMPLOYER_INVITE_NOTIFICATION:
      case NEW_INVITE_NOTIFICATION:
        // console.info(
        //   'branch NEW_EMPLOYER_INVITE_NOTIFICATION ',
        //   coldStartFinished
        // );

        if (!params.guid) break;

        try {
          await dispatch(fetchInviteAuthDetails(params.guid));
        } catch (e) {
          // if we cant get the details then dont show the invite
          // console.info('>>>> error fetching invite auth info', e)
          return;
        }

        // save the state here because if we have to log out then it'll all be cleared
        // so we set it again after log out
        const stateAfterFetch = getState();
        let inviteInfo = {
          email: stateAfterFetch.application?.authFormPrefills?.email || null,
          username: stateAfterFetch.application?.authFormPrefills?.username || null,
          employerMode: stateAfterFetch.application?.authFormPrefills?.employerMode || null,
          employerType:
            stateAfterFetch.screens?.application?.authFormPrefills?.employerType || null,
        };

        // if an email is specified in the invite params then we force the user to
        // log in with this email in order to accept the invite
        // if the user is logged in with a different email address than
        // they were invited to we need to log them out so they can
        // create a new account with the correct email
        const loggedInEmail = getState().entities?.userProfile?.email;
        const inviteEmail = inviteInfo?.email;
        if (inviteEmail && getState().user?.userId && inviteEmail !== loggedInEmail) {
          // if (true) {
          await dispatch(logoutUser(true));
          fullSync = false;
        }
        if (fullSync) {
          // we opened the app in a logged in state
          dispatch(_showInviteModal());
        } else if (__WEB__) {
          _showRegisterScreen();
        }

        break;
      default:
        return;
    }
  };
}

function _showInviteModal(cb) {
  return function (dispatch, getState) {
    setTimeout(() => {
      if (!getState().user.fullSync) return;
      Navigation.showModal({
        screen: 'InviteAccept',
        title: 'INVITATION',
        passProps: { cb },
      });
    }, 200);
  };
}

export function forceFullSync({ doLogout }) {
  return function (dispatch, getState) {
    // sets getState().user.fullSync to false
    dispatch({ type: FORCE_FULL_SYNC });
    if (doLogout || !getState().user?.userId) {
      //Nuke option, log out all users for this version of the app
      dispatch(logoutUser(false));
    } else {
      // clear state of everything except user Reducer
      dispatch({ type: CLEAR_ALL_STATE });
      startApp(dispatch, getState);
    }
  };
}

export function appInitialized() {
  return async function (dispatch, getState) {
    // get the last saved commit, store the current one
    let savedGitCommit;
    try {
      savedGitCommit = await fetchItem(GIT_COMMIT_KEY);
      if (savedGitCommit !== env(GIT_COMMIT)) {
        await storeItem(GIT_COMMIT_KEY, env(GIT_COMMIT));
      }
    } catch (e) {
      console.error('Error getting or storing GIT_COMMIT value: ', error);
    }

    // make sure the base URL we are logged in with is the correct current URL
    // if we're logged in with the wrong base URL do a hard logout
    const loggedInBaseUrl = getState().user.baseUrl;
    let doLogout = false;
    if (env(BASE_URL) !== loggedInBaseUrl) {
      doLogout = true;
    }

    // Next check the git commit - if this is a new version of the app
    // do a hard logout if it was configured in the env
    if (!savedGitCommit || savedGitCommit !== env(GIT_COMMIT)) {
      // force a logout only if the user is currently logged in
      doLogout = doLogout || (getState().user.fullSync && env(FORCE_USER_LOGOUT) === '1');
    }

    // doLogout = true;

    // always send it through "full sync" on startup
    // since each screen loads all data once anyway when it mounts this is
    // faster and more efficient than processing a lot of replay messages
    if (doLogout || getState().user.fullSync) {
      dispatch(forceFullSync({ doLogout }));
    } else {
      // we just logged in so fullSync is false, no need to clear the state
      startApp(dispatch, getState);
    }
  };
}

export async function startApp(dispatch, getState) {
  showSyncOverlay();
  const userId = getState().user.userId;
  // pull the user profilet to make sure we have employer data
  // (in case the redux state was cleared after login)
  // we can change this when the server decides what the return for the user
  // from the getAllCases service
  await dispatch(getUserInfoBasedOnProfileChange(userId));
  dispatch(getAllCasesAndEvents(userId))
    .then(() => {
      return sqlNow();
    })
    .then((timestampNow) => {
      dispatch(updateLatestWebsocketTimestamp(timestampNow));
      dispatch({ type: FULL_SYNC_SUCCESS });
      dispatch(dispatchColdStartFinished());
    })
    .catch((err) => {
      // probably the auth token expired
      dispatch(logoutUser(false));
    });
}

// if in any reason we've gotten logged out during startup
function _verifyFullSyncStatus(getState) {
  const fullSync = getState().user.fullSync;
  const authStatus = !!getState().user.userId;
  if (!fullSync) {
    if (authStatus) showSyncOverlay();
    return false;
  }
  return true;
}

// TODO: name this something better
function dispatchColdStartFinished() {
  return function (dispatch, getState) {
    Utils.initTrackingForUser(
      getState().entities.userProfile.email,
      getState().entities.userProfile
    );

    InteractionManager.runAfterInteractions(() => {
      let requestPermissionsFunc = async () => {
        // dont continue if fullsync has become false since we first came
        // into dispatchColdStartFinished will will come in here
        // here again after sync has completed and finish requesting perms
        if (!_verifyFullSyncStatus(getState)) return false;

        try {
          await dispatch(registerForNotifications());
        } catch (e) {
          // not sure what to do if there is an issue here, but dont crash
        }
        // dont continue if fullsync has become false since we first came
        // into dispatchColdStartFinished will will come in here
        // here again after sync has completed and finish requesting perms
        if (!_verifyFullSyncStatus(getState)) return false;
        if (_appVersionTimeoutId) {
          clearTimeout(_appVersionTimeoutId);
        }
        _appVersionTimeoutId = setTimeout(checkAndDisplayNewAppVersion, 2500);
      };

      const state = getState();
      const pendingInviteGuid = state.application.pendingInviteGuid;

      let needCallRequestPerms = true;

      // Uncomment for testing
      // _showInviteModal();

      // show invite and call requestPermissions
      if (pendingInviteGuid) {
        // if (true) {
        // console.info('dispatchColdStartFinished have pendingInviteGuid', pendingInviteGuid);

        const cb = needCallRequestPerms ? () => setTimeout(requestPermissionsFunc, 500) : null;
        dispatch(_showInviteModal(cb));
        needCallRequestPerms = false;
      }

      if (needCallRequestPerms) setTimeout(requestPermissionsFunc, 1000);

      dispatch({ type: COLD_START_FINISHED });
    });

    hideSyncOverlay();
  };
}

export function setLocalLanguagePreference(locale) {
  return function (dispatch, getState) {
    dispatch({ type: SET_LANGUAGE_PREF, payload: locale });
  };
}

export function updateLatestWebsocketTimestamp(timestamp) {
  return function (dispatch, getState) {
    dispatch({ type: WEBSOCKET_UPDATE_TIMESTAMP, payload: timestamp });
  };
}
